/**
 * The live connection to the server.
 *
 * One WebSocket per open terminal, opened against `/api/v1/stream`. There is no
 * token to pass: the upgrade is a same-origin GET carrying the session cookie,
 * which is the only reason this works at all — a browser's `new WebSocket(url)`
 * cannot set headers, so a bearer token would have nowhere to live.
 *
 * See ADR-0007.
 */

export type Tick = {
  symbol: string;
  bid: string;
  ask: string;
  last: string;
  as_of: string;
  /**
   * Whether the venue was open when this price was TAKEN, not whether it is now.
   *
   * `quoteToWire` has stamped it on every quote since M4/I1 and this type did
   * not declare it until M4/I6, so the terminal was throwing away the only fact
   * that distinguishes a market standing still from a market that is shut.
   */
  market_state: string;
};

/**
 * What prices this trader now, pushed rather than asked for.
 *
 * Carries no credential and no grant list: the terminal re-reads
 * `GET /api/v1/feeds` when it needs the full picture, and this frame exists to
 * say *that it should*, plus enough to write one honest line without waiting
 * for the round trip.
 */
export type FeedChange = {
  selected: string;
  /** What they had chosen, when it stopped being granted. */
  lapsed?: string;
  /** Why `selected` is theirs: `everyone`, `your_key`, `your_plan`. */
  because: string | null;
};

export type AccountEvent = {
  kind:
    | "filled"
    | "accepted"
    | "canceled"
    | "expired"
    | "replaced"
    | "rejected"
    | "stop_fired"
    /** A sync reset the account (`ADR-0037`); `order_id` is the sync id. */
    | "synced";
  order_id: string;
  client_order_id?: string;
  symbol: string;
  side: string;
  qty: string;
  status: string;
  filled_qty?: string;
  fill_price?: string;
  reason?: string;
};

/** The authoritative numbers. Everything the header shows between ticks derives from this. */
export type AccountSnapshot = {
  market_id: "US" | "SIM";
  base_currency: string;
  cash: string;
  available_cash: string;
  reserved_cash: string;
  equity: string;
  market_value: string;
  open_orders: number;
  positions: Array<{ symbol: string; qty: string; mark?: string; market_value?: string }>;
};

export type StreamHandlers = {
  onTicks: (ticks: Tick[]) => void;
  onAccount: (accountId: string, snapshot: AccountSnapshot, event: AccountEvent) => void;
  onStatus: (status: "connecting" | "live" | "offline") => void;
  /**
   * The server's own word on what this socket is watching — it caps the list.
   *
   * Reported upward rather than only mirrored here, because the shell draws the
   * ticker from its watched list: a subscription the server trimmed would
   * otherwise leave a row on screen that nothing will ever tick again.
   */
  onWatching: (symbols: string[]) => void;
  /** What prices this trader has changed. See `FeedChange`. */
  onFeed: (change: FeedChange) => void;
};

/** The close code the server uses for its 15-minute cap. Reconnect at once, not with backoff. */
const CLOSE_EXPIRED = 4001;

const MAX_BACKOFF_MS = 15_000;

export class Stream {
  #ws: WebSocket | undefined;
  #handlers: StreamHandlers;
  #symbols = new Set<string>();
  #closedByUs = false;
  #attempt = 0;
  #timer: ReturnType<typeof setTimeout> | undefined;

  constructor(handlers: StreamHandlers) {
    this.#handlers = handlers;
  }

  connect(): void {
    this.#closedByUs = false;
    this.#open();
  }

  /**
   * Close deliberately — on sign-out, or when the terminal goes away.
   *
   * Sign-out matters more than it looks. The socket authenticated once, at the
   * upgrade, and the server's 15-minute cap is a backstop rather than a prompt
   * response. Closing here is what makes signing out take effect immediately.
   */
  close(): void {
    this.#closedByUs = true;
    if (this.#timer) clearTimeout(this.#timer);
    this.#ws?.close();
    this.#ws = undefined;
    this.#handlers.onStatus("offline");
  }

  watch(symbols: string[]): void {
    for (const s of symbols) this.#symbols.add(s.toUpperCase());
    this.#send({ op: "watch", symbols: symbols.map((s) => s.toUpperCase()) });
  }

  unwatch(symbols: string[]): void {
    for (const s of symbols) this.#symbols.delete(s.toUpperCase());
    this.#send({ op: "unwatch", symbols: symbols.map((s) => s.toUpperCase()) });
  }

  watching(): string[] {
    return [...this.#symbols];
  }

  get live(): boolean {
    return this.#ws?.readyState === WebSocket.OPEN;
  }

  // ── internals ────────────────────────────────────────────────────────────

  #open(): void {
    this.#handlers.onStatus("connecting");

    const url = new URL("/api/v1/stream", window.location.href);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";

    const ws = new WebSocket(url);
    this.#ws = ws;

    ws.onopen = () => {
      this.#attempt = 0;
      this.#handlers.onStatus("live");
      // Re-declare the subscription: a reconnected socket is a *new* socket on
      // the server and remembers nothing. Without this, a terminal that
      // reconnects goes quiet and looks like a dead feed.
      if (this.#symbols.size > 0) {
        this.#send({ op: "watch", symbols: [...this.#symbols] });
      }
    };

    ws.onmessage = (event) => {
      let frame: Record<string, unknown>;
      try {
        frame = JSON.parse(String(event.data));
      } catch {
        return;
      }

      if (frame.type === "tick" && Array.isArray(frame.quotes)) {
        this.#handlers.onTicks(frame.quotes as Tick[]);
        return;
      }
      if (
        frame.type === "account" &&
        typeof frame.account_id === "string" &&
        frame.snapshot &&
        frame.event
      ) {
        this.#handlers.onAccount(
          frame.account_id,
          frame.snapshot as AccountSnapshot,
          frame.event as AccountEvent,
        );
        return;
      }
      if (frame.type === "feed" && typeof frame.selected === "string") {
        // What prices this trader has changed under them — a key revoked at the
        // broker, a plan lapsed, a choice made in another tab. Pushed since
        // M4/E52; before it the terminal only found out by being asked, so a
        // revoked key left the banner claiming `US` and the scrollback silent.
        this.#handlers.onFeed({
          selected: frame.selected,
          ...(typeof frame.lapsed === "string" ? { lapsed: frame.lapsed } : {}),
          because: typeof frame.because === "string" ? frame.because : null,
        });
        return;
      }
      if (frame.type === "watching" && Array.isArray(frame.symbols)) {
        // The server is the authority on what this socket is watching — it caps
        // the list. Mirroring its answer keeps `watch` from claiming a
        // subscription that was trimmed, and passing it up keeps the ticker from
        // holding a row the feed has stopped sending.
        this.#symbols = new Set(frame.symbols as string[]);
        this.#handlers.onWatching([...this.#symbols]);
      }
    };

    ws.onclose = (event) => {
      this.#ws = undefined;
      if (this.#closedByUs) return;
      this.#handlers.onStatus("offline");
      // The lifetime cap is an expected, routine event — reconnecting instantly
      // is right, and backing off would make the terminal stutter every 15
      // minutes for no reason.
      this.#schedule(event.code === CLOSE_EXPIRED ? 0 : undefined);
    };

    ws.onerror = () => {
      // `onclose` always follows, and it is the one that schedules the retry.
      // Doing it here as well would double every reconnection.
    };
  }

  #schedule(delayMs?: number): void {
    if (this.#timer) clearTimeout(this.#timer);
    const wait =
      delayMs ?? Math.min(MAX_BACKOFF_MS, 500 * 2 ** this.#attempt) * (0.7 + Math.random() * 0.6);
    this.#attempt++;
    this.#timer = setTimeout(() => this.#open(), wait);
  }

  #send(frame: Record<string, unknown>): void {
    if (this.#ws?.readyState === WebSocket.OPEN) this.#ws.send(JSON.stringify(frame));
  }
}
